import React, { useMemo } from "react";
import { useLocale } from "@/context/LocaleContext";
import { FlagCircle } from "@/components/Flag";
import { kickoffToDate } from "@/lib/kickoffTime";
import { formatMatchTeamLabel } from "@/lib/knockoutPlaceholderDisplay";
import { effectiveMatchScores, formatMatchScore } from "@/lib/matchScores";
import { MatchPenWinnerMark } from "@/components/MatchPenWinnerMark";
import { isMatchTipInputLocked } from "@/lib/matchTipsLocked";
import { CS_DISPLAY_ROW_COUNT } from "@/lib/harryBoy";
import { useMatchScoreDraft } from "@/lib/useMatchScoreDraft";

/** CS-kolumner: alltid CS_DISPLAY_ROW_COUNT när 1x2 eller CS finns; pad med – så tabellbredd blir densamma. */
function buildCsDisplayColumns(topScores, oddsLines) {
  const has1x2 = Array.isArray(oddsLines) && oddsLines.length > 0;
  const hasCs = Array.isArray(topScores) && topScores.length > 0;
  if (!has1x2 && !hasCs) return null;
  const cap = CS_DISPLAY_ROW_COUNT;
  const fromData = hasCs ? topScores.slice(0, cap) : [];
  const padLen = Math.max(0, cap - fromData.length);
  const pads = Array.from({ length: padLen }, (_, i) => ({ label: "–", _csPad: true, key: `cs-pad-${i}` }));
  return [...fromData.map((s, i) => ({ ...s, key: `cs-${i}` })), ...pads];
}

const fmt2 = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return "–";
  return n.toFixed(2);
};

/**
 * Match card row used in Group Stage and Knockout lists.
 * Expects oddsLines in same shape as ProjectPage builds.
 */
export const MatchRow = React.memo(function MatchRow({ m, bet, save, teamsByCode, logoByCode = {}, fixtures = [], oddsLines = null, topScores = null, knockoutUnlockUntil = null }) {
  const { lang, t } = useLocale();
  const locked = isMatchTipInputLocked(m, fixtures, knockoutUnlockUntil);
  const { home, setHome, away, setAway, onFocus, onBlur } = useMatchScoreDraft(
    bet,
    save,
    m.id,
    locked,
  );
  const loc = lang === "en" ? "en-GB" : "sv-SE";
  const { dateStr, timeStr } = useMemo(() => {
    const ko = kickoffToDate(m.kickoff);
    return {
      dateStr: ko.toLocaleDateString(loc, { day: "numeric", month: "short" }),
      timeStr: ko.toLocaleTimeString(loc, { hour: "2-digit", minute: "2-digit" }),
    };
  }, [m.kickoff, loc]);

  const homeFull = m.home_code ? teamsByCode[m.home_code] : m.home_placeholder || t("common.tbd");
  const awayFull = m.away_code ? teamsByCode[m.away_code] : m.away_placeholder || t("common.tbd");
  const home_name = formatMatchTeamLabel({
    code: m.home_code,
    placeholder: m.home_code ? null : m.home_placeholder,
    teamName: teamsByCode[m.home_code],
    tbdLabel: t("common.tbd"),
    lang,
  });
  const away_name = formatMatchTeamLabel({
    code: m.away_code,
    placeholder: m.away_code ? null : m.away_placeholder,
    teamName: teamsByCode[m.away_code],
    tbdLabel: t("common.tbd"),
    lang,
  });
  const csColumns = useMemo(() => buildCsDisplayColumns(topScores, oddsLines), [topScores, oddsLines]);
  const displayScore = formatMatchScore(m, lang);

  const scoreCls = `score-input max-sm:!w-10 max-sm:!h-10 max-sm:!text-lg max-sm:!min-w-0 ${locked ? "locked" : ""}`;

  return (
    <div className="glass glass-mobile-flat rounded-none sm:rounded-2xl w-full max-w-full min-w-0 px-3 py-4 sm:px-5" data-testid={`match-row-${m.id}`}>
      <div className="text-center text-[11px] sm:text-xs text-[var(--text-dim)] mb-3 font-mono leading-snug break-words px-0.5">
        {dateStr.toUpperCase()}, {timeStr} · <span className="uppercase">{m.venue}</span>
        {m.status === "LIVE" && (
          <span className="inline-flex items-center gap-1 ml-2 text-danger">
            <span className="live-dot" />
            {t("project.matchLive")}
          </span>
        )}
        {m.status === "FINISHED" && <span className="ml-2 text-[var(--accent-2)]">{t("project.matchFinished")}</span>}
      </div>

      <div className="grid w-full max-w-full min-w-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-2 sm:gap-x-4 gap-y-1">
        <div className="flex min-w-0 items-center justify-end gap-1.5 sm:gap-3">
          <div className="min-w-0 text-right">
            <div className="truncate font-semibold text-sm sm:text-base inline-flex items-center justify-end gap-1 max-w-full" title={homeFull}>
              <span className="truncate">{home_name}</span>
              <MatchPenWinnerMark match={m} teamCode={m.home_code} className="text-sm shrink-0" />
            </div>
          </div>
          <FlagCircle code={m.home_code} size={36} logoUrl={logoByCode[m.home_code]} />
        </div>

        <div className="flex w-[6.5rem] shrink-0 items-center justify-center gap-1 sm:w-[7.25rem] sm:gap-2">
          <input
            data-testid={`score-home-${m.id}`}
            className={scoreCls}
            type="number"
            min="0"
            value={home}
            disabled={locked}
            onChange={(e) => setHome(e.target.value)}
            onFocus={onFocus}
            onBlur={onBlur}
          />
          <span className="shrink-0 text-[var(--text-dim)]">:</span>
          <input
            data-testid={`score-away-${m.id}`}
            className={scoreCls}
            type="number"
            min="0"
            value={away}
            disabled={locked}
            onChange={(e) => setAway(e.target.value)}
            onFocus={onFocus}
            onBlur={onBlur}
          />
        </div>

        <div className="flex min-w-0 items-center justify-start gap-1.5 sm:gap-3">
          <FlagCircle code={m.away_code} size={36} logoUrl={logoByCode[m.away_code]} />
          <div className="min-w-0 text-left">
            <div className="truncate font-semibold text-sm sm:text-base inline-flex items-center gap-1 max-w-full" title={awayFull}>
              <MatchPenWinnerMark match={m} teamCode={m.away_code} className="text-sm shrink-0" />
              <span className="truncate">{away_name}</span>
            </div>
          </div>
        </div>
      </div>

      {(m.status === "FINISHED" || m.status === "LIVE") && displayScore && (
        <div className="text-center mt-3 text-xs font-mono">
          <span className="text-[var(--text-dim)]">{t("project.resultLabel")} </span>
          <span className="text-[var(--accent-2)] font-bold tracking-wide">
            {displayScore}
          </span>
        </div>
      )}

      {csColumns && (
        <div className="mt-3 pt-3 border-t border-[var(--line)] border-dashed">
          <details className="group" data-testid={`match-odds-${m.id}`}>
            <summary className="cursor-pointer select-none list-none">
              <div className="text-[10px] uppercase tracking-wider text-[var(--text-dim)] mb-2 text-center group-open:mb-2">
                {t("project.oddsCaption")}
                <span className="ml-2 text-[9px] normal-case tracking-normal text-[var(--text-dim)] opacity-80">(tryck för att visa)</span>
              </div>
            </summary>
            <div className="w-full overflow-x-auto sm:flex sm:justify-center">
              <table className="w-full max-w-full text-[10px] sm:text-xs font-mono table-fixed sm:max-w-xl">
                <thead>
                  <tr className="text-[var(--text-dim)]">
                    <th className="text-left font-normal pb-1 pr-1 w-[30%] sm:w-auto min-w-0">{t("project.oddsBook")}</th>
                    <th colSpan={3} className="pb-1 pl-0.5 pr-0 font-normal sm:pl-8 sm:pr-2 md:pl-10">
                      <div className="flex w-full justify-center font-mono tabular-nums">
                        <span className="inline-flex items-center justify-center gap-[0.4rem] sm:gap-2.5">
                          <span className="min-w-[1.6rem] text-center sm:min-w-[2rem]">1</span>
                          <span className="min-w-[1.6rem] text-center sm:min-w-[2rem]">X</span>
                          <span className="min-w-[1.6rem] text-center sm:min-w-[2rem]">2</span>
                        </span>
                      </div>
                    </th>
                    {csColumns.map((s, i) => (
                      <th key={s.key} className={`text-center font-normal pb-1 w-[2.05rem] sm:w-11 tabular-nums leading-tight shrink-0 ${i === 0 ? "sm:border-l-0" : ""}`}>
                        <span className="inline-block max-w-[2.75rem] truncate sm:max-w-none" title={s._csPad ? undefined : s.label}>
                          {s._csPad ? "–" : s.label}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(oddsLines || []).map((row) => (
                    <tr key={row.key} className="border-t border-[var(--line)]/60">
                      <td className="py-1.5 pr-1 text-[var(--text-dim)] truncate max-w-[7rem] sm:max-w-none min-w-0" title={row.title}>
                        {row.title}
                      </td>
                      <td colSpan={3} className="py-1.5 pl-0.5 pr-0 sm:pl-8 sm:pr-2 md:pl-10">
                        <div className="flex w-full justify-center tabular-nums">
                          <span className="inline-flex items-center justify-center gap-[0.4rem] sm:gap-2.5">
                            <span className="min-w-[1.6rem] text-center sm:min-w-[2rem]">{fmt2(row.home)}</span>
                            <span className="min-w-[1.6rem] text-center sm:min-w-[2rem]">{fmt2(row.draw)}</span>
                            <span className="min-w-[1.6rem] text-center sm:min-w-[2rem]">{fmt2(row.away)}</span>
                          </span>
                        </div>
                      </td>
                      {csColumns.map((sc, i) => {
                        let v = null;
                        if (!sc._csPad) {
                          if (row.key === "svenskaspel_se" && (sc.svenskaspel_se != null || sc.bet365 != null))
                            v = sc.svenskaspel_se ?? sc.bet365;
                          else if (row.key === "unibet_se" && sc.unibet_se != null) v = sc.unibet_se;
                        }
                        return (
                          <td
                            key={`cs-d-${row.key}-${sc.key}`}
                            className={`py-1.5 text-center tabular-nums text-[var(--text-dim)] w-[2.05rem] sm:w-11 shrink-0 ${i === 0 ? "sm:border-l-0" : ""}`}
                          >
                            {v != null ? fmt2(v) : "–"}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  <tr className="border-t-2 border-[var(--line)] bg-[var(--panel)]/40">
                    <td colSpan={4} className="py-1.5 pr-1 text-[var(--text-dim)] text-left">
                      {t("project.oddsAvgCsCaption")}
                    </td>
                    {csColumns.map((s, i) => (
                      <td
                        key={`cs-f-${s.key}`}
                        className={`py-1.5 text-center tabular-nums text-[var(--accent)] font-medium w-[2.05rem] sm:w-11 shrink-0 ${i === 0 ? "sm:border-l-0" : ""}`}
                      >
                        {s._csPad ? "–" : s.avg != null && s.avg !== "" ? fmt2(s.avg) : "–"}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </details>
        </div>
      )}
    </div>
  );
});

